import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home, ArrowRight, RefreshCw, HelpCircle, ArrowLeft, 
  AlertTriangle, Clock, CheckCircle2, XCircle, Activity,
  Mail, MessageSquare
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';



interface SystemStatus {
  name: string;
  status: 'operational' | 'degraded' | 'outage';
}

const systemStatuses: SystemStatus[] = [
  { name: 'Ad Builder', status: 'operational' },
  { name: 'Creative Delivery', status: 'operational' },
  { name: 'Analytics Dashboard', status: 'operational' },
  { name: 'API Services', status: 'operational' },
];

const StatusIndicator: React.FC<{ status: SystemStatus['status'] }> = ({ status }) => {
  const config = {
    operational: { color: 'bg-emerald-500', label: 'Operational', icon: CheckCircle2, textColor: 'text-emerald-600 dark:text-emerald-400' },
    degraded: { color: 'bg-amber-500', label: 'Degraded', icon: AlertTriangle, textColor: 'text-amber-600 dark:text-amber-400' },
    outage: { color: 'bg-red-500', label: 'Outage', icon: XCircle, textColor: 'text-red-600 dark:text-red-400' },
  };
  const { color, label, textColor } = config[status];

  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${color}`}>
        <div className={`w-2 h-2 rounded-full ${color} animate-ping`} />
      </div>
      <span className={`text-xs font-medium ${textColor}`}>{label}</span>
    </div>
  );
};

const ServerError: React.FC = () => {
  const navigate = useNavigate();
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [reportSent, setReportSent] = useState(false);

  useEffect(() => {
    console.error('500 Error: Server error encountered');
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryCount((prev) => prev + 1);
    
    // Simulate retry attempt
    await new Promise((resolve) => setTimeout(resolve, 2000));
    
    setIsRetrying(false);
    // In production, this would actually retry the failed request
    // For now, navigate to home after retry
    if (retryCount >= 1) {
      navigate('/');
    }
  };

  const handleReportIssue = () => {
    setReportSent(true);
    // In production, this would send an error report
    setTimeout(() => setReportSent(false), 5000);
  };

  const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />

      {/* Main Content */}
      <main className="relative pt-20 lg:pt-24">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 stripe-gradient-bg opacity-30" />
          <div className="absolute inset-0 grid-pattern opacity-30" />
          {/* Floating gradient orbs - using warmer tones for error state */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#635bff]/8 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#ff80b5]/10 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-[#00d4ff]/8 rounded-full blur-3xl animate-float-slow" />
        </div>

        {/* Hero Section */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <div className="order-2 lg:order-1">
              {/* Error Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 mb-6">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Error 500</span>
              </div>

              {/* Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white leading-tight mb-6">
                Something went{' '}
                <span className="bg-gradient-to-r from-[#635bff] via-[#ff80b5] to-[#635bff] bg-clip-text text-transparent">
                  wrong
                </span>
              </h1>

              {/* Description */}
              <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-4 max-w-lg">
                Our servers encountered an unexpected error. We've been notified and are working on a fix. Please try again in a moment.
              </p>

              {/* Error Details Card */}
              <div className="bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700 p-4 mb-8 max-w-md">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Error Details</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Error ID</span>
                    <code className="text-xs font-mono text-[#635bff] dark:text-[#00d4ff] bg-[#635bff]/10 dark:bg-[#635bff]/20 px-2 py-0.5 rounded">
                      {errorId}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Timestamp</span>
                    <span className="text-xs font-mono text-slate-600 dark:text-slate-300">
                      {currentTime.toISOString().replace('T', ' ').slice(0, 19)} UTC
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Status</span>
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Internal Server Error</span>
                  </div>
                  {retryCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 dark:text-slate-400">Retry Attempts</span>
                      <span className="text-xs font-mono text-slate-600 dark:text-slate-300">{retryCount}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Primary CTAs */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="stripe-button inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? 'Retrying...' : 'Try Again'}
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full hover:border-[#635bff] hover:text-[#635bff] transition-all shadow-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-[#635bff] bg-[#635bff]/10 dark:bg-[#635bff]/20 rounded-full hover:bg-[#635bff]/20 dark:hover:bg-[#635bff]/30 transition-all"
                >
                  <Home className="w-4 h-4" />
                  Return Home
                </Link>
              </div>

              {/* Report Issue */}
              <button
                onClick={handleReportIssue}
                disabled={reportSent}
                className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-[#635bff] dark:hover:text-[#635bff] transition-colors"
              >
                {reportSent ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <span className="text-emerald-600 dark:text-emerald-400">Report sent. Thank you!</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    <span>Report this issue</span>
                  </>
                )}
              </button>
            </div>


            {/* Right: White space */}
            <div className="order-1 lg:order-2 hidden lg:block" />

          </div>
        </section>

        {/* System Status Section */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24">
          <div className="bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Status Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Activity className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">System Status</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Current service health</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Updated {currentTime.toLocaleTimeString()}
                </span>
              </div>
            </div>

            {/* Status Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-200 dark:divide-slate-700">
              {systemStatuses.map((service) => (
                <div key={service.name} className="px-6 py-4">
                  <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{service.name}</p>
                  <StatusIndicator status={service.status} />
                </div>
              ))}
            </div>

            {/* Status Footer */}
            <div className="px-6 py-3 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                All systems operational. This error may be temporary.
              </p>
              <a
                href="https://status.adventive.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-[#635bff] hover:underline"
              >
                View full status page
              </a>
            </div>
          </div>
        </section>

        {/* What to Do Section */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24">
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              What you can do
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Here are some steps that might help resolve the issue.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700">
              <div className="w-10 h-10 rounded-xl bg-[#635bff]/10 dark:bg-[#635bff]/20 flex items-center justify-center mb-4">
                <RefreshCw className="w-5 h-5 text-[#635bff]" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Refresh the page</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                This error may be temporary. Wait a few seconds and try refreshing the page.
              </p>
            </div>

            <div className="p-6 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700">
              <div className="w-10 h-10 rounded-xl bg-[#00d4ff]/10 dark:bg-[#00d4ff]/20 flex items-center justify-center mb-4">
                <Clock className="w-5 h-5 text-[#00d4ff]" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Try again later</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                If the problem persists, wait a few minutes. Our team may be deploying a fix.
              </p>
            </div>

            <div className="p-6 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700">
              <div className="w-10 h-10 rounded-xl bg-[#ff80b5]/10 dark:bg-[#ff80b5]/20 flex items-center justify-center mb-4">
                <MessageSquare className="w-5 h-5 text-[#ff80b5]" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Contact support</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                If you keep seeing this error, reach out to our support team with the error ID above.
              </p>
            </div>
          </div>
        </section>

        {/* Support CTA Banner */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24">
          <div className="bg-gradient-to-br from-[#635bff] to-[#7c3aed] rounded-3xl p-8 md:p-12 overflow-hidden relative">
            {/* Background pattern */}
            <div className="absolute inset-0 grid-pattern opacity-10" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#00d4ff]/20 rounded-full blur-3xl" />
            
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Need immediate assistance?
                </h3>
                <p className="text-white/70 max-w-lg">
                  Our support team is available to help resolve any issues. Include the error ID when contacting us for faster resolution.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 flex-shrink-0">
                <Link
                  to="/resources/help"
                  className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-white bg-white/15 border border-white/25 rounded-full hover:bg-white/25 transition-all"
                >
                  <HelpCircle className="w-4 h-4" />
                  Help Center
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-[#635bff] bg-white rounded-full hover:bg-white/90 transition-all"
                >
                  Contact Support
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServerError;
